import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const MILESTONES_GBP = [100, 500, 1000, 2500, 5000, 10000] as const;

/**
 * Email the creator when one of their commissions is approved/earned.
 * Safe to call from webhooks; never throws — logs and returns on error.
 */
export async function notifyCommissionEarned(commissionId: string) {
  try {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row } = await supabaseAdmin
      .from("commissions")
      .select("creator_id, creator_amount, commission_amount, currency, product_id")
      .eq("id", commissionId)
      .maybeSingle();
    if (!row) return;
    const [{ data: creator }, { data: product }] = await Promise.all([
      supabaseAdmin.from("creators").select("user_id, name").eq("id", row.creator_id).maybeSingle(),
      row.product_id
        ? supabaseAdmin.from("products").select("title").eq("id", row.product_id).maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    if (!creator?.user_id) return;
    const { data: userInfo } = await supabaseAdmin.auth.admin.getUserById(creator.user_id);
    const email = userInfo?.user?.email;
    if (!email) return;
    const amt = Number(row.creator_amount ?? row.commission_amount ?? 0);
    const currency = (row.currency || "GBP").toUpperCase();
    const sym = currency === "GBP" ? "£" : currency === "USD" ? "$" : currency === "EUR" ? "€" : "";
    const { sendTemplatedEmail } = await import("./email/send.functions");
    await sendTemplatedEmail(email, {
      name: "commission_earned",
      data: {
        name: creator.name ?? "there",
        amount: `${sym}${amt.toFixed(2)}`,
        product: product?.title ?? "your pick",
      },
    });
  } catch (e) {
    console.error("[commission_earned] email failed", e);
  }
}

/**
 * Server-only helper. For each milestone the creator has crossed (based on
 * `totalEarnings`), insert a `milestone_reached` notification (idempotent —
 * skips milestones that already have a notification row) and send a
 * congratulatory email. Safe to call from webhooks and payout flows.
 */
export async function checkAndCreateMilestoneNotification(
  creatorId: string,
  totalEarnings: number,
): Promise<{ created: number[] }> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

  const { data: creator } = await supabaseAdmin
    .from("creators")
    .select("user_id, name")
    .eq("id", creatorId)
    .maybeSingle();
  if (!creator?.user_id) return { created: [] };

  const { data: existing } = await supabaseAdmin
    .from("notifications")
    .select("message")
    .eq("user_id", creator.user_id)
    .eq("type", "milestone_reached");

  const alreadyNotified = new Set<number>();
  for (const row of existing ?? []) {
    for (const m of MILESTONES_GBP) {
      if (typeof row.message === "string" && row.message.includes(`£${m}`)) {
        alreadyNotified.add(m);
      }
    }
  }

  const crossed = MILESTONES_GBP.filter((m) => totalEarnings >= m && !alreadyNotified.has(m));
  if (crossed.length === 0) return { created: [] };

  await supabaseAdmin.from("notifications").insert(
    crossed.map((amount) => ({
      user_id: creator.user_id as string,
      type: "milestone_reached",
      message: `🎉 You just hit £${amount} in total earnings! Amazing work.`,
    })),
  );

  // Look up email and send a celebration email for each newly crossed milestone.
  try {
    const { data: userInfo } = await supabaseAdmin.auth.admin.getUserById(creator.user_id);
    const email = userInfo?.user?.email;
    if (email) {
      const { sendTemplatedEmail } = await import("./email/send.functions");
      for (const amount of crossed) {
        await sendTemplatedEmail(email, {
          name: "milestone",
          data: { name: creator.name ?? "there", amount: String(amount) },
        });
      }
    }
  } catch (e) {
    console.error("[milestone] email send failed", e);
  }

  return { created: [...crossed] };
}

export const listMyNotifications = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { data, error } = await supabase
      .from("notifications")
      .select("id, type, message, read, created_at")
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(10);
    if (error) throw new Error(error.message);
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("read", false);
    return { items: data ?? [], unreadCount: count ?? 0 };
  });

export const markAllNotificationsRead = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("notifications")
      .update({ read: true })
      .eq("user_id", userId)
      .eq("read", false);
    if (error) throw new Error(error.message);
    return { ok: true };
  });